import logger from 'loglevel';
import { getEnv, getOrchestratorHost, getKameleoonVisitorCode } from 'common/selectors';
import { call, select, CallEffect, SelectEffect } from 'redux-saga/effects';
import { api, ApiRequestConfig } from 'api';
import { getLang } from 'utils';
import { addKameleoonCookieToString } from 'utils/kameleoon/cookie';

function* getQueryParams(init?: Record<string, string | undefined>) {
  const lang: string = yield call(getLang);
  const qp = init ? { ...init, lang } : { lang };
  return new URLSearchParams(qp).toString();
}

export function* post<Response = Record<string, unknown>, Data extends object = Record<string, unknown>>(
  path: string,
  data: Data,
  config?: ApiRequestConfig,
  queryParams?: Record<string, string | undefined>,
  customHost?: string,
): Generator<unknown, Response, Response> {
  const params = yield getQueryParams(queryParams);
  return yield* doRequest<Response, Data>('post', `${path}?${params}`, data, config, customHost);
}

export function* put<Response = Record<string, unknown>, Data extends object = Record<string, unknown>>(
  path: string,
  data: Data,
  config?: ApiRequestConfig,
  queryParams?: Record<string, string>,
  customHost?: string,
): Generator<unknown, Response, Response> {
  const params = yield getQueryParams(queryParams);
  return yield* doRequest<Response, Data>('put', `${path}?${params}`, data, config, customHost);
}

export function* get<Response = unknown, Data extends object = Record<string, unknown>>(
  path: string,
  data: Data,
  config: ApiRequestConfig = {},
  customHost?: string,
) {
  const lang: string = yield call(getLang);
  return yield* doRequest<Response, Data>('get', path, { ...data, lang }, config, customHost);
}

export function* del<Response = unknown, Data extends object = Record<string, unknown>>(
  path: string,
  data: Data,
  config: ApiRequestConfig = {},
  customHost?: string,
) {
  const lang: string = yield call(getLang);
  return yield* doRequest<Response, Data>('delete', path, { ...data, lang }, config, customHost);
}

export function* doRequest<Response, Data extends object>(
  method: 'get' | 'post' | 'put' | 'delete',
  path: string,
  data: Data,
  config: ApiRequestConfig = {},
  customHost?: string,
): Generator<CallEffect<unknown> | SelectEffect, Response> {
  const { USE_CREDENTIALS, requestID, API_KEY } = (yield select(getEnv)) as ReturnType<typeof getEnv>;
  const ORCHESTRATOR_HOST = yield select(getOrchestratorHost);
  const kameleoonVisitorCode: string = yield select(getKameleoonVisitorCode);

  const HOST = customHost ? customHost : ORCHESTRATOR_HOST;

  config.withCredentials = USE_CREDENTIALS;

  if (__IS_SERVER__) {
    config.headers = USE_CREDENTIALS ? { ...config.headers } : config.headers;
  }

  if (requestID) {
    config.headers = { ...config.headers, 'x-request-id': requestID };
  }

  if (API_KEY) {
    config.headers = { ...config.headers, 'x-api-key': API_KEY };
  }

  if (kameleoonVisitorCode) {
    const cookieHeader = (config?.headers?.Cookie as string) || '';
    const modifiedCookie = addKameleoonCookieToString(cookieHeader, kameleoonVisitorCode);
    config.headers = { ...config.headers, Cookie: modifiedCookie };
  }

  const apiMethod = api[method];

  return yield call(apiMethod, buildUrl(HOST, path), data, config);
}

export function buildUrl(base: string, part: string) {
  try {
    return new URL(part, base).toString();
  } catch (e) {
    logger.error((e as Error).message);
    return '';
  }
}
